import { getter } from '../../utils/proxyGetter'

// Enumerating the possible statuses a turnstile can encounter during its lifecycle.
export const TURNSTILE_STATUSES = {
  LOADED: 'loaded', // Signifies that the turnstile component is initialized and operational.
  VERIFIED: 'verified', // Indicates successful user authentication through the turnstile challenge.
  ERROR: 'error', // Denotes an issue occurred during the turnstile operation.
  EXPIRED: 'expired', // Signals that the turnstile challenge has reached its expiration limit.
  TIMEOUT: 'timeout', // Triggers when the turnstile challenge exceeds the expected response time.
  UNSUPPORTED: 'unsupported', // Indicates the user's browser lacks necessary features for the challenge.
  BEFORE_INTERACTIVE: 'before interactive', // Pre-interaction state, signaling the imminent start of the challenge.
}

// Mapping event triggers to their corresponding turnstile statuses.
export const TURNSTILE_EVENTS_CONFIG = {
  onLoad: [TURNSTILE_STATUSES.LOADED], // Event fired after the turnstile is fully loaded and ready.
  onVerify: [TURNSTILE_STATUSES.VERIFIED, 'add'], // Event activated upon successful verification, possibly hinting at further actions.
  onError: [TURNSTILE_STATUSES.ERROR], // Event triggered during an error in the turnstile process.
  onExpire: [TURNSTILE_STATUSES.EXPIRED], // Event fired when the challenge expires.
  onTimeout: [TURNSTILE_STATUSES.TIMEOUT], // Event triggered for timeouts during the challenge.
  onUnsupported: [TURNSTILE_STATUSES.UNSUPPORTED], // Event fired for unsupported browsers.
  onBeforeInteractive: [TURNSTILE_STATUSES.BEFORE_INTERACTIVE], // Event before the challenge turns interactive.
}

// Localized messages for tooltips based on the turnstile's current status.
export const TURNSTILE_TOOLTIP_MESSAGES = {
  UNSUPPORTED: 'Please update your browser', // Suggestion to update the browser for compatibility.
  NEED_VERIFICATION: 'security_check_message', // Generic verification prompt.
}

// Handling proxy errors with specific codes and messages.
// TODO keep it for future
export const TURNSTILE_PROXY_ERROR_LIST = new Proxy(
  {
    '100xxx': {
      message: 'Initialization Problems',
    },
    '102xxx|103xxx|104xxx|106xxx': {
      message: 'Invalid Parameters',
    },
    '105xxx': {
      message: 'Turnstile API Compatibility',
    },
    '110100|110110': {
      message: 'Invalid sitekey',
    },
    110200: {
      message: 'Unknown domain',
    },
    110420: {
      message: 'Invalid action',
    },
    110430: {
      message: 'Invalid cData',
    },
    110500: {
      message: 'Unsupported browser', // Indicates browser support issues.
    },
    110510: {
      message: 'Inconsistent user-agent',
    },
    '11060x|11062x': {
      message: 'Challenge timed out',
    },
    '120xxx': {
      message: 'Internal Errors for Cloudflare Employees',
    },
    200010: {
      message: 'Invalid caching', // User-related caching issues.
    },
    200100: {
      message: 'Time problem', // Time-related issues encountered by users.
    },
    '300xxx': {
      message: 'Generic client execution error',
    },
    '600xxx': {
      message: 'Challenge execution failure',
      button: {
        method: 'reload',
        text: 'Reset',
      },
    },
    default: {
      message: TURNSTILE_TOOLTIP_MESSAGES.NEED_VERIFICATION,
    },
  },
  {
    get: getter,
  },
)

// Keys for controlling the visibility of passes, blocks, and challenges during development/testing.
export const LOCALHOST_TURNSTILE_SITE_KEYS = {
  PASSES_VISIBLE: '1x00000000000000000000AA', // Key for showing passes in tests.
  BLOCK_VISIBLE: '2x00000000000000000000AB', // Key for displaying blocks in tests.
  PASSES_INVISIBLE: '1x00000000000000000000BB', // Key for hiding passes in tests.
  BLOCKS_INVISIBLE: '2x00000000000000000000BB', // Key for concealing blocks in tests.
  CHALLENGE_VISIBLE: '3x00000000000000000000FF', // Key for making challenges visible in tests.
}

// Configuration settings for the turnstile, including UI theme and refresh behavior.
export const TURNSTILE_CONFIG = {
  className: 'turnstile',
  retry: 'never',
  refreshTimeout: 'never',
  appearance: 'always',
  refreshExpired: 'manual', // Manual refresh for expired items for controlled management.
  theme: 'light', // Sets the UI theme to light mode.
}

// Name of a cookie used to store turnstile-related state or preferences.
export const TURNSTILE_COOKIE_NAME = 'trnstl'

// Production host URL for accessing the turnstile service.
export const PROD_HOST = 'app.neilpatel.com'

// Key for storing the selected development stage site key in local storage.
export const TURNSTILE_DEVSTAGE_LOCALSTORAGE_KEY = 'trnstl_dev_key'
